import React, { useState, useMemo, useEffect } from 'react';
import { 
  Box, Typography, Paper, CircularProgress, 
  TextField, Button, Alert,
  RadioGroup, Radio, FormControlLabel, FormControl, 
  Checkbox, FormGroup, Select, MenuItem,
  LinearProgress, Fade, Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';

export default function PublicQuestionnaire({
  template,
  onSubmitSuccess,
  isSubmitting = false,
  mode,
  templateId,
  clientId,
  initialResponseData,
  responseIdToUpdate,
  onClose,
  user,
  initialResponses,
  scrollContainerRef
}) {

  // ✅ Initialize with passed responses (draft resume)
  const [responses, setResponses] = useState(initialResponses || {});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const isViewMode = mode === 'view';

  // ---------------------------------------------------------------------------
  // Flatten questions into a per-question array, preserving section metadata
  // ---------------------------------------------------------------------------
  const flatQuestions = useMemo(() => {
    if (!template?.questions) return [];

    let currentSectionTitle = '';
    let currentSectionDescription = '';

    return template.questions.map((question, index) => {
      const newSection = question.sectionTitle?.trim();
      let isFirstInSection = false;

      if (newSection && newSection !== currentSectionTitle) {
        currentSectionTitle = newSection;
        currentSectionDescription = question.sectionDescription?.trim() || '';
        isFirstInSection = true;
      } else if (newSection && newSection === currentSectionTitle) {
        // Same section title repeated — not a new section
        isFirstInSection = false;
      }

      return {
        ...question,
        uniqueKey: question.questionId || question.id || `question_${index}`,
        resolvedSectionTitle: currentSectionTitle,
        resolvedSectionDescription: isFirstInSection ? currentSectionDescription : '',
        isFirstInSection,
      };
    });
  }, [template?.questions]);

  const totalQuestions = flatQuestions.length;
  const isOnConfirmation = currentIndex >= totalQuestions;
  const currentQuestion = !isOnConfirmation ? flatQuestions[currentIndex] : null;

  // Count answered questions
  const answeredCount = useMemo(() => {
    return flatQuestions.filter(q => {
      const val = responses[q.uniqueKey];
      if (Array.isArray(val)) return val.length > 0;
      return val && (typeof val !== 'string' || val.trim() !== '');
    }).length;
  }, [flatQuestions, responses]);

  // Progress percentage (0–100)
  const progressPercent = totalQuestions > 0
    ? Math.round((currentIndex / totalQuestions) * 100)
    : 0;

  // Scroll to top on question change
  useEffect(() => {
    if (scrollContainerRef?.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentIndex, scrollContainerRef]);

  // ---------------------------------------------------------------------------
  // Answer handling
  // ---------------------------------------------------------------------------
  const handleAnswerChange = (question, value) => {
    if (isViewMode) return;

    const key = question.uniqueKey;
    setResponses(prev => ({ ...prev, [key]: value }));

    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------
  const handleNext = () => {
    // Validate current question if required
    if (currentQuestion?.isRequired) {
      const key = currentQuestion.uniqueKey;
      const val = responses[key];
      if (!val || (typeof val === 'string' && val.trim() === '')) {
        setErrors(prev => ({ ...prev, [key]: 'This field is required' }));
        return;
      }
    }
    setCurrentIndex(prev => Math.min(prev + 1, totalQuestions));
  };

  const handleBack = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  // ---------------------------------------------------------------------------
  // Draft save
  // ---------------------------------------------------------------------------
  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      await onSubmitSuccess(responses, false);
    } catch (error) {
      console.error('[PublicQuestionnaire] Draft save failed:', error);
      toast.error(`Failed to save draft: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSavingDraft(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Final submission
  // ---------------------------------------------------------------------------
  const handleSubmit = () => {
    if (isViewMode) return;

    // Validate all required questions
    let hasErrors = false;
    const newErrors = {};
    flatQuestions.forEach(q => {
      if (q.isRequired) {
        const val = responses[q.uniqueKey];
        if (!val || (typeof val === 'string' && val.trim() === '')) {
          newErrors[q.uniqueKey] = 'This field is required';
          hasErrors = true;
        }
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      // Navigate to first question with an error
      const firstErrorIndex = flatQuestions.findIndex(q => newErrors[q.uniqueKey]);
      if (firstErrorIndex >= 0) setCurrentIndex(firstErrorIndex);
      toast.error('Please answer all required questions before submitting.');
      return;
    }

    console.log('[PublicQuestionnaire] Submitting responses:', responses);
    onSubmitSuccess(responses, true);
  };

  // ---------------------------------------------------------------------------
  // Render a single question by type (preserved from original)
  // ---------------------------------------------------------------------------
  const renderQuestion = (question) => {
    const key = question.uniqueKey;
    const value = responses[key] || '';
    const hasError = !!errors[key];
    const questionType = question.questionType?.toLowerCase().replace(/[_\s]/g, '') || '';

    // View mode: show answer as read-only text
    if (isViewMode) {
      let displayValue = Array.isArray(value) ? value.join(', ') : value;
      if (!displayValue) {
        displayValue = <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>No answer provided.</span>;
      }
      return (
        <Box>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              backgroundColor: '#f9f9f9',
              p: 2,
              border: '1px solid #ddd',
              borderRadius: 1,
              width: '100%',
              minHeight: '48px',
              fontSize: '1rem',
              lineHeight: 1.5,
            }}
          >
            {displayValue}
          </Typography>
        </Box>
      );
    }

    switch (questionType) {
      case 'shorttext':
      case 'longtext':
        return (
          <FormControl fullWidth>
            <TextField
              placeholder={question.placeholderText || 'Type your answer here...'}
              value={value}
              onChange={(e) => handleAnswerChange(question, e.target.value)}
              error={hasError}
              helperText={errors[key]}
              multiline
              minRows={questionType === 'longtext' ? 4 : 2}
              fullWidth
              name={key}
              autoFocus={!isViewMode}
              sx={{
                '& .MuiInputBase-root': { fontSize: '1rem' },
                '& textarea': { overflow: 'hidden', resize: 'none' },
              }}
            />
          </FormControl>
        );
      case 'multiplechoice':
        return (
          <FormControl component="fieldset" error={hasError}>
            <RadioGroup value={value} onChange={(e) => handleAnswerChange(question, e.target.value)} name={key}>
              {question.options?.map((option, index) => (
                <FormControlLabel key={index} value={option} control={<Radio />} label={option} />
              ))}
            </RadioGroup>
            {hasError && <Typography variant="caption" color="error">{errors[key]}</Typography>}
          </FormControl>
        );
      case 'checkbox':
        return (
          <FormControl component="fieldset" error={hasError}>
            <FormGroup>
              {question.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={(value || []).includes(option)}
                      onChange={(e) => {
                        const currentValues = value || [];
                        const newValues = e.target.checked
                          ? [...currentValues, option]
                          : currentValues.filter(v => v !== option);
                        handleAnswerChange(question, newValues);
                      }}
                      name={`${key}_${index}`}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
            {hasError && <Typography variant="caption" color="error">{errors[key]}</Typography>}
          </FormControl>
        );
      case 'dropdown':
        return (
          <FormControl fullWidth error={hasError}>
            <Select
              value={value}
              onChange={(e) => handleAnswerChange(question, e.target.value)}
              displayEmpty
              name={key}
            >
              <MenuItem value="" disabled>
                <em>Select an option...</em>
              </MenuItem>
              {question.options?.map((option, index) => (
                <MenuItem key={index} value={option}>{option}</MenuItem>
              ))}
            </Select>
            {hasError && <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{errors[key]}</Typography>}
          </FormControl>
        );
      case 'date':
        return (
          <FormControl fullWidth>
            <TextField
              type="date"
              value={value}
              onChange={(e) => handleAnswerChange(question, e.target.value)}
              error={hasError}
              helperText={errors[key]}
              InputLabelProps={{ shrink: true }}
              name={key}
            />
          </FormControl>
        );
      default:
        return <Typography color="error">Unsupported question type: {question.questionType}</Typography>;
    }
  };

  // ---------------------------------------------------------------------------
  // Guard: template not loaded
  // ---------------------------------------------------------------------------
  if (!template || !template.questions) {
    return (
      <Alert severity="error">
        Unable to load questionnaire template.
      </Alert>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER: Confirmation / Review Screen (after last question)
  // ---------------------------------------------------------------------------
  if (isOnConfirmation) {
    return (
      <Paper elevation={0} sx={{ p: 3 }}>
        {/* Progress bar — complete */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={100}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': { borderRadius: 3 }
            }}
          />
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: 3
        }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />

          <Typography variant="h6" gutterBottom>
            {isViewMode ? 'Review Complete' : 'Ready to Submit'}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {answeredCount} of {totalQuestions} questions answered
          </Typography>

          {!isViewMode && answeredCount < totalQuestions && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Unanswered questions are optional — you can always update your responses later.
            </Typography>
          )}

          {isViewMode && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Questionnaire review complete.
            </Typography>
          )}
        </Box>

        {/* Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
          <Button
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: 'none' }}
          >
            Back
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          {!isViewMode && (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleSaveDraft}
                disabled={isSubmitting || isSavingDraft}
                startIcon={<SaveIcon />}
                sx={{ textTransform: 'none' }}
              >
                {isSavingDraft ? 'Saving...' : 'Save Draft'}
              </Button>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? null : <SendIcon />}
                sx={{ textTransform: 'none', minWidth: 160 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Submit & Activate'}
              </Button>
            </>
          )}
        </Box>
      </Paper>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER: Single Question View
  // ---------------------------------------------------------------------------
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      {/* ── Progress Bar + Counter ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Question {currentIndex + 1} of {totalQuestions}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {answeredCount} answered
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              transition: 'transform 0.3s ease'
            }
          }}
        />
      </Box>

      {/* ── Section Header (shown on first question of a new section) ── */}
      {currentQuestion.isFirstInSection && currentQuestion.resolvedSectionTitle && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={currentQuestion.resolvedSectionTitle}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500, mb: currentQuestion.resolvedSectionDescription ? 1 : 0 }}
          />
          {currentQuestion.resolvedSectionDescription && (
            <Alert severity="info" sx={{ mt: 1 }} icon={false}>
              <Typography variant="body2">{currentQuestion.resolvedSectionDescription}</Typography>
            </Alert>
          )}
        </Box>
      )}

      {/* ── Non-first questions: subtle section context ── */}
      {!currentQuestion.isFirstInSection && currentQuestion.resolvedSectionTitle && (
        <Typography variant="caption" color="text.disabled" sx={{ mb: 1, display: 'block' }}>
          {currentQuestion.resolvedSectionTitle}
        </Typography>
      )}

      {/* ── Question Text ── */}
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', lineHeight: 1.5 }}>
        {currentQuestion.questionText}
        {currentQuestion.isRequired && !isViewMode && (
          <span style={{ color: 'red' }}> *</span>
        )}
        {!currentQuestion.isRequired && !isViewMode && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            (optional)
          </Typography>
        )}
      </Typography>

      {/* ── Question Input ── */}
      <Box sx={{ mb: 3 }}>
        {renderQuestion(currentQuestion)}
      </Box>

      {/* ── Navigation ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Button
          onClick={handleBack}
          disabled={currentIndex === 0}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        {!isViewMode && (
          <Button
            variant="text"
            color="secondary"
            onClick={handleSaveDraft}
            disabled={isSubmitting || isSavingDraft}
            size="small"
            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
          >
            {isSavingDraft ? 'Saving...' : 'Save Draft'}
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={currentIndex === totalQuestions - 1 ? <CheckCircleOutlineIcon /> : <ArrowForwardIcon />}
          sx={{ textTransform: 'none', minWidth: 120 }}
        >
          {currentIndex === totalQuestions - 1
            ? (isViewMode ? 'Review' : 'Review & Submit')
            : 'Next'
          }
        </Button>
      </Box>
    </Paper>
  );
}